import React, { useEffect, useState } from 'react';
import DashboardHeader from './components/DashboardHeader';
import FiltersBar from './components/FiltersBar';
import MetricsCards from './components/MetricsCards';
import ChartsSection from './components/ChartsSection';
import ItemsChartsSection from './components/ItemsChartsSection';
import OrdersChartsSection from './components/OrdersChartsSection';

import { apiFetch } from '../../../utils/api';
import { useDarkMode } from '../../../hooks/useDarkMode';

const KDSAnalytics = () => {
  const { isDark, setDarkMode } = useDarkMode('reportsDarkMode');

  const handleToggleDarkMode = () => {
    const nextDark = !isDark;
    setDarkMode(nextDark);
  };
  // === Overview states ===
  const [ordersByDay, setOrdersByDay] = useState([]);
  const [avgBumpTimeByDay, setAvgBumpTimeByDay] = useState([]);
  const [ordersHeatmap, setOrdersHeatmap] = useState([]);
  const [itemsData, setItemsData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);

  // === Orders tab states ===
  const [ordersByTable, setOrdersByTable] = useState([]);
  const [ordersByServer, setOrdersByServer] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState({});
  const [ordersList, setOrdersList] = useState([]);
  const [ordersItemsCount, setOrdersItemsCount] = useState([]);
  const [ordersBumpMinutes, setOrdersBumpMinutes] = useState([]);

  const [activeTab, setActiveTab] = useState('Overview');

  const [filters, setFilters] = useState({
    site_id: '',
    location_id: '',
    sublocation_id: '',
    station: '',
    daypart: '',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    site_id: '',
    location_id: '',
    sublocation_id: '',
    station: '',
    daypart: '',
    startDate: '',
    endDate: ''
  });
  const [hasSeededDefaultStartDate, setHasSeededDefaultStartDate] = useState(false);

  useEffect(() => {
    fetchReports(filters);
  }, []);

  const handleRefresh = () => {
    const defaultFilters = {
      site_id: '',
      location_id: '',
      sublocation_id: '',
      station: '',
      daypart: '',
      startDate: '',
      endDate: ''
    };
    setFilters(defaultFilters);
    fetchReports(defaultFilters);
  };

  const buildQueryString = (filters) => {
    const params = new URLSearchParams();
    if (filters.site_id) params.append('site_id', filters.site_id);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.sublocation_id) params.append('sublocation_id', filters.sublocation_id);
    if (filters.station) params.append('station', filters.station);
    if (filters.daypart) params.append('daypart', filters.daypart);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return params.toString();
  };

  const fetchReports = async (activeFilters = filters) => {
    try {
      const query = buildQueryString(activeFilters);
      const fetchJson = async (path) => {
        const res = await apiFetch(path);
        const raw = await res.text();
        if (!res.ok) {
          throw new Error(`${path} -> HTTP ${res.status}: ${raw.slice(0, 200)}`);
        }
        try {
          return raw ? JSON.parse(raw) : null;
        } catch {
          throw new Error(`${path} -> Invalid JSON: ${raw.slice(0, 200)}`);
        }
      };

      const [
        ordersByDayRes,
        avgBumpTimeByDayRes,
        ordersHeatmapRes,
        itemsSummaryRes,
        ordersByTableRes,
        ordersByServerRes,
        ordersSummaryRes,
        ordersListRes,
        ordersItemsCountRes,
        ordersBumpMinutesRes
      ] = await Promise.all([
        fetchJson(`/updates-reports/orders-by-day?${query}`),
        fetchJson(`/updates-reports/avg-bump-time-by-day?${query}`),
        fetchJson(`/updates-reports/orders-heatmap?${query}`),
        fetchJson(`/updates-reports/items-summary?${query}`),

        // Orders tab data
        fetchJson(`/updates-reports/orders-by-table?${query}`),
        fetchJson(`/updates-reports/orders-by-server?${query}`),
        fetchJson(`/updates-reports/orders-summary?${query}`),
        fetchJson(`/updates-reports/orders-list?${query}`),
        fetchJson(`/updates-reports/orders-items-count?${query}`),
        fetchJson(`/updates-reports/orders-bump-minutes?${query}`),
      ]);

      // === Overview
      setOrdersByDay(Array.isArray(ordersByDayRes) ? ordersByDayRes : []);
      setAvgBumpTimeByDay(Array.isArray(avgBumpTimeByDayRes) ? avgBumpTimeByDayRes : []);
      setOrdersHeatmap(Array.isArray(ordersHeatmapRes) ? ordersHeatmapRes : []);
      setItemsData(Array.isArray(itemsSummaryRes) ? itemsSummaryRes : []);

      const total = (Array.isArray(ordersByDayRes) ? ordersByDayRes : []).reduce(
        (sum, day) => sum + Number(day.total_orders || 0), 0
      );
      setTotalOrders(total);
      setAppliedFilters(activeFilters);

      // Seed default start date once: first order date in system (for Avg Orders/Day baseline).
      if (!hasSeededDefaultStartDate && !activeFilters.startDate && Array.isArray(ordersByDayRes) && ordersByDayRes.length) {
        const firstDate = ordersByDayRes
          .map((d) => String(d?.date || '').slice(0, 10))
          .filter(Boolean)
          .sort()[0];
        if (firstDate) {
          const next = { ...activeFilters, startDate: firstDate };
          setFilters(next);
          setAppliedFilters(next);
          setHasSeededDefaultStartDate(true);
        }
      }

      // === Orders tab
      setOrdersByTable(Array.isArray(ordersByTableRes) ? ordersByTableRes : []);
      setOrdersByServer(Array.isArray(ordersByServerRes) ? ordersByServerRes : []);
      setOrdersSummary(ordersSummaryRes || {});
      setOrdersList(Array.isArray(ordersListRes) ? ordersListRes : []);
      setOrdersItemsCount(Array.isArray(ordersItemsCountRes) ? ordersItemsCountRes : []);
      setOrdersBumpMinutes(Array.isArray(ordersBumpMinutesRes) ? ordersBumpMinutesRes : []);

    } catch (err) {
      console.error(' Error fetching reports:', err);
      setOrdersByDay([]);
      setAvgBumpTimeByDay([]);
      setOrdersHeatmap([]);
      setItemsData([]);
      setTotalOrders(0);

      setOrdersByTable([]);
      setOrdersByServer([]);
      setOrdersSummary({});
      setOrdersList([]);
      setOrdersItemsCount([]);
      setOrdersBumpMinutes([]);
    }
  };

  return (
    <div className="min-h-screen p-6 font-sans bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 dark:text-gray-100">
      <DashboardHeader
        onRefresh={handleRefresh}
        isDark={isDark}
        onToggleDarkMode={handleToggleDarkMode}
      />

      <FiltersBar
        filters={filters}
        onChange={setFilters}
        onApply={(latestFilters) => fetchReports(latestFilters)}
      />

      <div className="mt-6">
        <MetricsCards
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />


        {activeTab === 'Orders' && (
          <OrdersChartsSection
            isDark={isDark}
            filters={appliedFilters}
            ordersByDay={ordersByDay}
            ordersByTable={ordersByTable}
            ordersByServer={ordersByServer}
            ordersSummary={ordersSummary}
            ordersList={ordersList}
            ordersItemsCount={ordersItemsCount}
            ordersBumpMinutes={ordersBumpMinutes}
          />
        )}

        {activeTab === 'Items' && (
          <ItemsChartsSection
            data={itemsData}
            totalOrders={totalOrders}
            isDark={isDark}
            filters={appliedFilters} />

        )}
        {activeTab === 'Overview' && (
          <ChartsSection
            isDark={isDark}
            ordersByDay={ordersByDay}
            avgBumpTimeByDay={avgBumpTimeByDay}
            ordersHeatmap={ordersHeatmap}
            itemsData={itemsData}
            ordersList={ordersList}
            ordersBumpMinutes={ordersBumpMinutes}
          />
        )}

      </div>
    </div>
  );
};

export default KDSAnalytics;
